<template>
    <li class="program-block__item margin--l-xl" :class="{ 'featured': program.featured }">
        <a 
            class="program-block" 
            :href="`${programUrl}${program.id}/`" 
            @click.prevent="goToProgramPage"
            @keydown.enter.prevent="goToProgramPage"
            
        >
            <figure>
                <img :src="program.img.src" :alt="program.img.title" width="100"/>
            </figure>
            <div class="program-block__content">
                <div class="program-block__label" v-if="program.featured">
                    <p>{{ printString('featured') }}</p>
                </div>
                <h3 class="step--2">{{ program.program_name }}</h3>
                <div class="program-block__content__details">
                    <span v-if="program.university" class="program-block__content__details__item">
                        <p class="paragraph--teaser">{{ program.university }}</p>
                    </span>
                    <span v-if="program.location" class="program-block__content__details__item">
                        <p class="paragraph--teaser">{{ program.location }}</p>
                    </span>
                </div>
            </div>
        </a>
        <div class="program-block__buttons">
            <Menu v-if="isDashboard" :theme="tooltipTheme" :container="menuContainer" :options="menuOptions">
                <button 
                    class="general-button only-icon copy-link-button"
                    @click.stop="copyLink"
                    @keydown.enter.prevent="copyLink"
                    
                >
                    <span class="vh">{{ printString('copy_link') }}</span>
                    <span v-html="ico_gallery_16"/>
                </button>

                <template #popper>
                    <div>
                        {{ copyLinkTooltipText }}
                    </div>
                </template>
            </Menu>
            <Menu :theme="tooltipTheme" :container="menuContainer" :options="menuOptions">
                <button 
                    class="general-button only-icon" 
                    :class="{ 
                        'compared': isCompared,
                        'disabled': isCompareListFull && !isCompared
                    }" 
                    @click.stop="handleCompare"
                    @keydown.enter.prevent="handleCompare"
                    
                    :disabled="isCompareListFull && !isCompared"
                >
                    <span class="vh">{{ isCompared ? 'Remove from compare' : 'Add to compare' }}</span>
                    <span v-html="ico_compare_16"/>
                </button>

                <template #popper>
                    <div v-if="isCompareListFull && !isCompared">
                        {{ printString('compare_list_full') }}
                    </div>
                    <div v-else>
                        {{ compareButtonText }}
                    </div>
                </template>
            </Menu>

            <Menu :theme="tooltipTheme" :container="menuContainer" :options="menuOptions">
                <button 
                    class="general-button only-icon"
                    :class="{ 
                        'saved': isSaved,
                        'disabled': isSavedListFull && !isSaved
                    }"
                    @click.stop="handleSave"
                    @keydown.enter.prevent="handleSave"
                
                    :disabled="isSavedListFull && !isSaved"
                >
                    <span class="vh">{{ isSaved ? 'Remove from favorites' : 'Add to favorites' }}</span>
                    <span v-if="isSaved && isDashboard" v-html="ico_close_16"/>
                    <span v-if="isSaved && !isDashboard" v-html="ico_favorite_16_checked"/>
                    <span v-if="!isSaved" v-html="ico_favorite_16"/>
                </button>

                <template #popper>
                    <div v-if="!is_logged_in">
                        <a class="secondary-button red-color" :href="loginUrl" @click.stop>
                            {{ printString('sign_in') }}
                        </a>
                        {{ printString('to_add_to_list') }}
                    </div>
                    <div v-else-if="isSavedListFull && !isSaved">
                        {{ printString('saved_list_full') }}
                    </div>
                    <div v-else>
                        {{ saveButtonText }}
                    </div>
                </template>
            </Menu>

            <div v-if="isDashboard" class="general-button only-icon reorder-button">
                <span v-html="ico_reorder_16" ></span>
                <span class="vh">Reorder</span>
            </div>
        </div>
        <div v-if="isDashboard" class="general-button only-icon reorder-button reorder-mobile">
            <span v-html="ico_reorder_16" ></span>
            <span class="vh">Reorder</span>
        </div>
    </li>
</template>

<script>
import { Menu } from 'floating-vue'

export default {
    components: {
        Menu,
    },
    data() {
        return {
            ico_compare_16: window.ico_compare_16,
            ico_favorite_16: window.ico_favorite_16,
            ico_favorite_16_checked: window.ico_favorite_16_checked,
            ico_close_16: window.ico_close_16,
            is_logged_in: window.is_logged_in,
            ico_reorder_16: window.ico_reorder_16,
            ico_gallery_16: window.ico_gallery_16,
            loginUrl: window.log_in_url,
            saveButtonText: this.printString('save'),
            compareButtonText: this.printString('compare'),
            tooltipTheme: 'tooltips',
            menuContainer: null,
            programUrl: window.program_url,
            copyLinkTooltipText: this.printString('copy_link'), // New property for copy link tooltip text
        };
    },
    props: {
        program: {
            type: Object,
            required: true,
        },
        savedList: {
            type: Array,
            required: true,
        },
        compareList: {
            type: Array,
            required: true,
        },
        isDashboard: {
            type: Boolean,
            default: false,
        },
        maxCompareItems: {
            type: Number,
            default: 3,
        },
        maxSavedItems: {
            type: Number,
            default: 10,
        },
    },
    computed: {
        isSaved() {
            const saved = this.savedList.includes(this.program.id);
            this.saveButtonText = saved ? this.printString('remove') : this.printString('save');
            return saved;
        },
        isCompared() {
            const compared = this.compareList.includes(this.program.id);
            this.compareButtonText = compared ? this.printString('to_remove_from_compare') : this.printString('compare');
            return compared;
        },
        isCompareListFull() {
            return this.compareList.length >= this.maxCompareItems;
        },
        tooltipOptions() {
            if (this.is_logged_in) {
                return {
                    save: {
                        content: this.saveButtonText,
                        theme: 'tooltips',
                    },
                    compare: {
                        content: this.compareButtonText,
                        theme: 'tooltips',
                    }
                };
            } else {
                return {
                    save: {
                        content: `<div><a href="${this.loginUrl}">${this.printString('sign_in')}</a> ${this.printString('to_add_to_list')}</div>`,
                        html: true,
                        theme: 'tooltips',
                        triggers: ['hover', 'focus', 'touch'],
                        hideOnClick: false,
                    },
                    compare: {
                        content: `<div><a href="${this.loginUrl}">${this.printString('sign_in')}</a> ${this.printString('to_add_to_compare')}</div>`,
                        html: true,
                        theme: 'tooltips',
                        triggers: ['hover', 'focus', 'touch'],
                        hideOnClick: false,
                    }
                };
            }
        },
        menuOptions() {
            return {
                triggers: ['hover', 'focus', 'touch'],
                hideTriggers: ['hover', 'touch'],
                popperTriggers: ['hover', 'focus', 'focusin'],
            };
        },
        isSavedListFull() {
            return this.savedList.length >= this.maxSavedItems;
        },
    },
    mounted() {
        this.menuContainer = this.$el.querySelector('.program-block__buttons');
    },
    methods: {
        goToProgramPage(event) {
            event.preventDefault(); // Ensure the default link behavior is prevented
            // const routeName = window.lang === 'fr' ? 'programSingleFr' : 'programSingle';
            const routeName = window.lang === 'fr' ? 'programSingle' : 'programSingle';
            this.$router.push({ name: routeName, params: { id: this.program.id } });
        },
        addToCompare(event) {
            event.stopPropagation(); // Prevent the whole block from being clickable when clicking buttons
            this.$emit("add-to-compare", this.program.id);
        },
        handleCompare(event) {
            event.stopPropagation();
            if (!this.isCompared && this.isCompareListFull) {
                console.warn(`Compare list is full. Max ${this.maxCompareItems} schools allowed.`);
                return;
            }
            this.addToCompare(event);
        },
        saveToList(event) {
            // console.log("saveToList");
            // console.log(this.program.id);
            event.stopPropagation();
            this.$emit("save-to-list", this.program.id);
        },
        handleSave(event) {
            event.stopPropagation();
            if (!this.is_logged_in) {
                window.location.href = this.loginUrl;
                return;
            } else {
                if (!this.isSaved && this.isSavedListFull) {
                    console.warn(`Saved list is full. Max ${this.maxSavedItems} schools allowed.`);
                    return;
                }
                this.saveToList(event);
            }
        },
        copyLink() {
            const url = `${this.programUrl}${this.program.id}/`;
            navigator.clipboard.writeText(url).then(() => {
                this.copyLinkTooltipText = this.printString('copy_link_success');
                setTimeout(() => {
                    this.copyLinkTooltipText = this.printString('copy_link');
                }, 2000); // Reset the tooltip text after 2 seconds
            }).catch(err => {
                console.error('Failed to copy link: ', err);
                this.copyLinkTooltipText = this.printString('copy_link_error');
            });
        },
        printString(string) {
            return window.translations[string];
        },
    },
};
</script>

